/**
 * SystemEventBus — single shared SSE consumer for /api/system/stats.
 *
 * Mirrors ops_bus.js / hls_bus.js. Subscribes to zapd's `system` channel
 * (Phase E1.1) which emits `{event:'tick', data:{cpu_pct, rss_mib,
 * loadavg1, gpu, n_sessions, sessions:[…]}}` once per second when at
 * least one subscriber is connected (broker auto-pauses otherwise).
 *
 * Single consumer right now: the player debug-overlay (D-toggle).
 * Future: a /admin/system/dashboard could reuse the same bus.
 */

const RECENT_EVENTS_CAP = 60; // ~1 minute at 1 Hz

class SystemEventBus {
  constructor() {
    this.es = null;
    this.subscribers = new Set();
    this.connStateCbs = new Set();
    this.connState = 'idle';
    /** Newest-first ring of recent ticks. Late-mounted consumers can
     *  read [0] for the most recent snapshot. */
    this.recentEvents = [];
    /** Most recent tick — convenience accessor for the overlay. */
    this.latest = null;
  }

  start() {
    if (this.es) return;
    this._setConnState('connecting');
    this.es = new EventSource('/api-zapd/system/stats');
    this.es.addEventListener('open', () => this._setConnState('open'));
    this.es.addEventListener('error', () => this._setConnState('error'));
    this.es.onmessage = (m) => {
      let evt;
      try {
        evt = JSON.parse(m.data);
      } catch (_) {
        return;
      }
      this._dispatch(evt);
    };
  }

  stop() {
    if (this.es) {
      this.es.close();
      this.es = null;
    }
    this.subscribers.clear();
    this.connStateCbs.clear();
    this.recentEvents.length = 0;
    this.latest = null;
    this._setConnState('idle');
  }

  /**
   * Subscribe to every tick. The bus already polls only one EventSource;
   * subscribers can filter inside their handler if needed.
   * @param {(evt:object)=>void} handler
   * @returns {()=>void} unsubscribe
   */
  subscribe(handler) {
    this.subscribers.add(handler);
    return () => this.subscribers.delete(handler);
  }

  onConnState(cb) {
    this.connStateCbs.add(cb);
    cb(this.connState);
    return () => this.connStateCbs.delete(cb);
  }

  /** Most recent tick, or null if none yet. */
  getLatest() {
    return this.latest;
  }

  _dispatch(evt) {
    this.latest = evt;
    this.recentEvents.unshift(evt);
    if (this.recentEvents.length > RECENT_EVENTS_CAP) {
      this.recentEvents.length = RECENT_EVENTS_CAP;
    }
    for (const handler of this.subscribers) {
      try {
        handler(evt);
      } catch (e) {
        console.error('systemBus handler error', e);
      }
    }
  }

  _setConnState(state) {
    this.connState = state;
    for (const cb of this.connStateCbs) {
      try {
        cb(state);
      } catch (_) {}
    }
  }
}

export const systemBus = new SystemEventBus();
